import { DateBefore, DateRange, OnSelectHandler } from 'react-day-picker';
import { useEffect, useState } from 'react';
import { addMonths, isSunday, nextSunday, previousSunday } from 'date-fns';
import { dateToLocalISOString } from '../utils/calendarUtils';
import useGetAvailableDates from '@my-pages/features/object-booking/hooks/useGetAvailableDates.ts';
import useGetSelectedPeriod from '@my-pages/features/object-booking/hooks/useGetSelectedPeriod.ts';
import { usePostApiV2ObjectBookingObjectsObjectIdAvailablePeriodsForMonth } from '@shared/integrations/taf-api/v2/features/object-booking/object-booking';
import { CalendarRangePickerSettings } from '@my-pages/features/calendar/types';
import CalendarRangeDatePicker from '@my-pages/features/calendar/components/CalendarRangeDatePicker.tsx';
import AccommodationPeriodInformation from '@my-pages/features/object-booking/components/AccommodationPeriodInformation.tsx';
import AccommodationConfirmBookingForm from '@my-pages/features/object-booking/components/AccommodationConfirmBookingForm.tsx';
import { AccommodationBookingGroupPageContentResponseModel } from '@shared/integrations/umbraco-taf-delivery-api/models';
import AlertBox from '@shared/components/AlertBox.tsx';
import Markup from '@shared/components/Markup.tsx';
import { useParams } from '@tanstack/react-router';
import { removeLastUrlSegment } from '@my-pages/utils/urlUtils.ts';

interface IAccommodationBookingForm {
    bookingObjectId: string;
    cabinGroup: AccommodationBookingGroupPageContentResponseModel;
}

const today = new Date();
const startBookingDate = nextSunday(today);
const beforeMatcher: DateBefore = { before: startBookingDate };

const AccommodationBookingForm = ({ bookingObjectId, cabinGroup }: IAccommodationBookingForm) => {
    const { _splat } = useParams({ strict: false });
    const bookingGroupUrl = removeLastUrlSegment(_splat);
    const { mutate, data } = usePostApiV2ObjectBookingObjectsObjectIdAvailablePeriodsForMonth();
    const availableDates = useGetAvailableDates(data);
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>();
    const selectedPeriod = useGetSelectedPeriod(selectedRange, data);

    const handleOnSelectClick: OnSelectHandler<DateRange | undefined> = (_, triggerDate) => {
        const startDate = isSunday(triggerDate) ? triggerDate : previousSunday(triggerDate);
        const endDate = nextSunday(startDate);
        setSelectedRange({ from: startDate, to: endDate });
    };

    const handleGetAvailablePeriods = (date: Date) => {
        mutate({ objectId: bookingObjectId, data: { bookingId: bookingObjectId, startDate: dateToLocalISOString(date) } });
    };

    const settings: CalendarRangePickerSettings = {
        disabled: beforeMatcher,
        startMonth: startBookingDate,
        endMonth: cabinGroup.properties?.numberOfFeatureMonth ? addMonths(startBookingDate, cabinGroup.properties.numberOfFeatureMonth) : undefined,
        modifiers: { available: availableDates },
        selected: selectedRange,
        onSelect: handleOnSelectClick,
        onMonthChange: handleGetAvailablePeriods,
    };

    useEffect(() => {
        handleGetAvailablePeriods(startBookingDate);
    }, []);

    return (
        <>
            {bookingGroupUrl ? (
                <>
                    {cabinGroup.properties?.calendarBookingRules ? (
                        <AlertBox theme='info'>
                            <Markup content={cabinGroup.properties.calendarBookingRules.markup} />
                        </AlertBox>
                    ) : null}
                    <CalendarRangeDatePicker settings={settings} />
                    <AccommodationConfirmBookingForm
                        handleGetAvailablePeriods={handleGetAvailablePeriods}
                        period={selectedPeriod}
                        bookingGroupUrl={bookingGroupUrl}
                    />
                    {selectedPeriod ? <AccommodationPeriodInformation selectedPeriod={selectedPeriod} /> : null}
                </>
            ) : (
                <AlertBox theme={'danger'}>Det går för tillfället inte att göra några bokningar</AlertBox>
            )}
        </>
    );
};
export default AccommodationBookingForm;
